import { useCallback, useEffect, useState } from 'react';

import { type Agent } from '@atlassian/conversation-assistant-service';
import { useActivityClient } from '@atlassian/recent-work-client';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { fetchAggResponse } from '../../common';
import { createAtlAttributionHeader } from '../../common/fetch/atl-attribution-header';

// Define the types for the two API responses
export type TeamsData = {
	teamSearch: {
		nodes: Array<{
			team: {
				id: string;
				organizationId: string;
				membershipSettings: string;
				description: string;
				displayName: string;
				state: string;
				largeAvatarImageUrl: string;
				smallAvatarImageUrl: string;
				largeHeaderImageUrl: string;
				smallHeaderImageUrl: string;
				isVerified: boolean;
				members: {
					nodes: Array<{
						member: {
							accountStatus: string;
							name: string;
							picture: string;
							id: string;
						};
					}>;
				};
			};
			memberCount: number;
			includesYou: boolean;
		}>;
		pageInfo: {
			hasNextPage: boolean;
			endCursor?: string;
		};
	};
};

export type AgentActivityData = {
	agentId: string;
	recentActivity: Array<{
		id: string;
		title: string;
		description?: string;
		timestamp: string;
		// Add more activity fields as needed
	}>;
	totalActivityCount: number;
};

// Define the GraphQL queries (these would be replaced with actual queries)
export const KNOWLEDGE_CARD_AGENT_TEAMS_OPERATION_NAME = 'KnowledgeCardAgentTeamsQuery';
const KNOWLEDGE_CARD_AGENT_TEAMS_QUERY = `
  query ${KNOWLEDGE_CARD_AGENT_TEAMS_OPERATION_NAME}(
    $first: Int,
    $after: String,
    $organizationId: ID!,
    $query: String = "",
    $memberIds: [ID] = [],
    $sortBy: [TeamSort],
    $siteId: String!,
    $showEmptyTeams: Boolean = false
  ) {
    team {
      teamSearch: teamSearchV2(
        first: $first
        after: $after
        organizationId: $organizationId
        filter: { query: $query, membership: { memberIds: $memberIds } }
        sortBy: $sortBy
        siteId: $siteId
        showEmptyTeams: $showEmptyTeams
      ) @optIn(to: ["Team-search-v2"]) {
        nodes {
          ...TeamNodeV2
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  fragment TeamNodeV2 on TeamSearchResultV2 {
		team {
			id
			organizationId
			membershipSettings
			description
			displayName
			state
			largeAvatarImageUrl
			smallAvatarImageUrl
			largeHeaderImageUrl
			smallHeaderImageUrl
			isVerified
			members {
				nodes {
					member {
						id
						accountStatus
						name
						picture
					}
				}
			}
		}
		memberCount
		includesYou
	}
`;

// Define the variables types for each query
export type TeamsVariables = {
	first: number;
	after?: string;
	organizationId: string;
	query?: string;
	memberIds?: string[];
	sortBy?: string[];
	siteId: string;
	showEmptyTeams?: boolean;
};

export type AgentActivityVariables = {
	agentId: string;
	cloudId: string;
	limit: number;
};

// Define the response types
export type TeamsResponse = {
	team: TeamsData;
};

export type AgentActivityResponse = {
	agentActivity: AgentActivityData;
};

// Define the hook return type
export type UseAgentCardDataReturn = {
	// Loading states
	loading: boolean;
	teamsLoading: boolean;
	activityLoading: boolean;

	// Data
	teamsData: TeamsData | null;
	activityData: AgentActivityData | null;

	// Error states
	error: string | null;
	teamsError: string | null;
	activityError: string | null;

	// Refetch function
	refetch: () => void;
};

export const useAgentCardData = (agent: Agent | undefined): UseAgentCardDataReturn => {
	const { commonAttributes, sourceProduct } = useAIAnswerContext();
	const { cloudId, workspaceId, orgId } = commonAttributes;

	// Loading states
	const [loading, setLoading] = useState(false);
	const [teamsLoading, setTeamsLoading] = useState(false);
	const [activityLoading, setActivityLoading] = useState(false);

	// Data states
	const [teamsData, setTeamsData] = useState<TeamsData | null>(null);
	const [activityData, setActivityData] = useState<any | null>(null);

	// Error states
	const [error, setError] = useState<string | null>(null);
	const [teamsError, setTeamsError] = useState<string | null>(null);
	const [activityError, setActivityError] = useState<string | null>(null);

	// Refetch counter to trigger refetches
	const [refetchCounter, setRefetchCounter] = useState(0);

	// Fetch teams data
	const fetchTeamsData = useCallback(async () => {
		if (!agent?.id || !cloudId) {
			return;
		}

		setTeamsLoading(true);
		setTeamsError(null);

		try {
			const response = await fetchAggResponse<TeamsVariables, TeamsResponse>({
				graphQuery: KNOWLEDGE_CARD_AGENT_TEAMS_QUERY,
				variables: {
					first: 12,
					organizationId: `ari:cloud:platform::org/${orgId}`,
					siteId: cloudId,
					showEmptyTeams: true,
					memberIds: [agent.identity_account_id?.split('/').pop() || ''],
				},
				operationName: KNOWLEDGE_CARD_AGENT_TEAMS_OPERATION_NAME,
				headers: createAtlAttributionHeader({
					atlWorkspaceId: workspaceId,
					tenantId: cloudId,
					product: sourceProduct ?? '',
				}),
			});

			const { data, errors } = response;

			if (errors && errors.length > 0) {
				throw new Error(errors.map((e) => e.message).join(', '));
			}

			if (data?.team) {
				setTeamsData(data.team);
			} else {
				throw new Error('No teams data received');
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to fetch teams data';
			setTeamsError(errorMessage);
			setError(errorMessage);
		} finally {
			setTeamsLoading(false);
		}
	}, [agent?.id, agent?.identity_account_id, cloudId, workspaceId, sourceProduct, orgId]);

	const activityClient = useActivityClient('v3', 'people');

	// Fetch activity data
	const fetchActivityData = useCallback(async () => {
		if (!agent?.identity_account_id) {
			return;
		}

		setActivityLoading(true);
		setActivityError(null);

		try {
			const activityData = await activityClient.fetchActivities(
				[100],
				{
					limit: 50,
					actors: [
						agent.identity_account_id.startsWith('ari:cloud:identity::user/')
							? agent.identity_account_id
							: `ari:cloud:identity::user/${agent.identity_account_id}`,
					],
				},
				['workedOn'],
				false,
			);

			setActivityData(activityData.workedOn);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'Failed to fetch activity data';
			setActivityError(errorMessage);
			setError(errorMessage);
		} finally {
			setActivityLoading(false);
		}
	}, [agent?.identity_account_id, activityClient]);

	// Combined loading state - true if either API is loading
	const combinedLoading = teamsLoading || activityLoading;

	// Refetch function
	const refetch = useCallback(() => {
		setRefetchCounter((prev) => prev + 1);
	}, []);

	// Effect to fetch data when agent changes or refetch is triggered
	useEffect(() => {
		if (!agent?.id) {
			// Reset all states when no agent is provided
			setTeamsData(null);
			setActivityData(null);
			setTeamsError(null);
			setActivityError(null);
			setError(null);
			return;
		}

		// Reset error states when starting new fetch
		setError(null);

		// Fetch data conditionally based on available dependencies
		const fetchData = async () => {
			const promises = [];

			// Only fetch teams if we have the required data
			if (agent.identity_account_id && cloudId && orgId) {
				promises.push(fetchTeamsData());
			}

			// Only fetch activity if we have the required data
			if (agent.identity_account_id) {
				promises.push(fetchActivityData());
			}

			// Execute available fetches in parallel
			if (promises.length > 0) {
				await Promise.all(promises);
			}
		};

		fetchData();
	}, [
		agent?.id,
		agent?.identity_account_id,
		cloudId,
		orgId,
		fetchTeamsData,
		fetchActivityData,
		refetchCounter,
	]);

	// Update combined loading state
	useEffect(() => {
		setLoading(combinedLoading);
	}, [combinedLoading]);

	return {
		loading,
		teamsLoading,
		activityLoading,
		teamsData,
		activityData,
		error,
		teamsError,
		activityError,
		refetch,
	};
};
